import * as React from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Control,
  FieldErrors,
  FieldValues,
  Resolver,
  useForm,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment';
import { rtkQApi } from 'lib/Api';
import { TableParamsInterface } from 'commons/types';
import { UploadFile } from 'antd';
import setFormFieldMutationError from 'utils/form/setFormFieldMutationError';
import { configCourseTableFilters } from 'utils/filtersHelpers';
import { waiversApi } from 'lib/Api/Waivers';

// Generic interfaces for table parameters
type EntityType =
  | 'instructor'
  | 'term'
  | 'donationAsk'
  | 'course'
  | 'user_courses'
  | 'invite'
  | 'program'
  | 'contentProvider'
  | 'lmsInstance'
  | 'location'
  | 'enrollment'
  | 'membershipSubscription'
  | 'waiver';

// this is based on the useQuery options from rtkq, an existing options type is not exported
// https://redux-toolkit.js.org/rtk-query/api/created-api/hooks#usequery-signature
type EntityOptions = {
  pollingInterval?: number;
  skipPollingIfUnfocused?: boolean;
  refetchOnReconnect?: boolean;
  refetchOnFocus?: boolean;
  skip?: boolean;
  refetchOnMountOrArgChange?: boolean | number;
};

interface UseEntityTableProps {
  entityType: EntityType;
  queryOptions?: EntityOptions;
  queryArgs?: any;
}

// Return type for the useEntityTable hook
interface UseEntityTable<T> {
  searchInputRef: React.RefObject<any>;
  tableParams: TableParamsInterface;
  setTableParams: React.Dispatch<React.SetStateAction<TableParamsInterface>>;
  selectedEntity: T | undefined;
  setSelectedEntity: React.Dispatch<React.SetStateAction<T | undefined>>;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteEntityModalOpen: boolean;
  setIsDeleteEntityModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isFetching: boolean;
  isLoading: boolean;
  paginatedEntities?: {
    data: T[];
    pagination: {
      page: number;
      perPage: number;
      total: number;
    };
  };
  handleSearchChange: (value: string) => void;
  handleEdit: (entity: T) => void;
  handleDelete: (entity: T) => void;
  handleDropdownClick: (e: any, record: T) => void;
  handleAdd: () => void;
  handleModalClose: () => void;
  handleDeleteConfirm: () => Promise<void>;
  handleReset: () => void;
}

export function useEntityTable<T extends Record<string, any>>({
  entityType,
  queryOptions,
  queryArgs = {},
}: UseEntityTableProps): UseEntityTable<T> {
  let { partnerId } = useParams<{ partnerId: string }>();
  const { data: globalPartner } = useSelector((state) => state.partnerInstance?.globalPartner);
  const history = useHistory();
  const [tableParams, setTableParams] = React.useState<TableParamsInterface>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });
  const [selectedEntity, setSelectedEntity] = React.useState<T | undefined>();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isDeleteEntityModalOpen, setIsDeleteEntityModalOpen] = React.useState(false);

  let filters;
  let fields;
  let getMutation;
  let deleteMutation;
  let handleAdd = (): void => {
    setSelectedEntity(undefined);
    setIsModalOpen(true);
  };

  let handleEdit = (term: T): void => {
    setSelectedEntity(term);
    setIsModalOpen(true);
  };

  switch (entityType) {
    case 'instructor':
      getMutation = rtkQApi.endpoints.getPaginatedInstructors.useQuery;
      deleteMutation = rtkQApi.endpoints.deleteInstructor.useMutation;
      break;

    case 'term':
      getMutation = rtkQApi.endpoints.getPaginatedTerms.useQuery;
      deleteMutation = rtkQApi.endpoints.deleteTerm.useMutation;
      break;

    case 'invite':
      getMutation = rtkQApi.endpoints.getPaginatedInvites.useQuery;
      deleteMutation = rtkQApi.endpoints.deleteInvite.useMutation;
      filters = tableParams?.filters;
      break;

    case 'donationAsk':
      getMutation = rtkQApi.endpoints.getPaginatedDonationAsks.useQuery;
      deleteMutation = rtkQApi.endpoints.deleteDonationAsk.useMutation;
      break;

    case 'course':
      getMutation = rtkQApi.endpoints.getPaginatedCourses.useQuery;
      deleteMutation = rtkQApi.endpoints.deletePartnerCourse.useMutation;
      partnerId = globalPartner?.id.toString();
      handleAdd = () => {
        history.push(`/instance/${partnerId}/courses/create`);
      };
      handleEdit = (entity: T): void => {
        history.push(`/instance/${partnerId}/courses/create?id=${entity.id}`);
      };
      filters = configCourseTableFilters(
        tableParams?.filters || [],
        globalPartner,
        queryArgs?.onlyActiveCourses,
      );
      fields = [
        'id',
        'categories',
        'course_availability',
        'course_code',
        'course_count',
        'course_state',
        'end_date',
        'enrollment_count',
        'is_fed_net_course',
        'is_active',
        'is_lms_hosted',
        'is_waitlist_enabled',
        'max_num_of_enrollments',
        'min_num_of_enrollments',
        'modality',
        'parent_id',
        'schedules',
        'section_code',
        'slug',
        'start_date',
        'title',
        'partner.id',
        'partner.name',
        'partner.partner_id',
        'term.id',
        'term.name',
        'timezone',
        'lms_access_end_date',
        'lms_indefinite_access',
      ];
      break;

    case 'user_courses':
      getMutation = rtkQApi.endpoints.getUserPaginatedCourses.useQuery;
      deleteMutation = () => [() => Promise.resolve({}), { data: null, isLoading: false }];
      partnerId = globalPartner?.id.toString();
      filters = configCourseTableFilters(tableParams?.filters || [], globalPartner);
      fields = [
        'id',
        'categories',
        'course_availability',
        'course_code',
        'course_count',
        'course_state',
        'end_date',
        'enrollment_count',
        'is_fed_net_course',
        'is_active',
        'is_lms_hosted',
        'is_waitlist_enabled',
        'max_num_of_enrollments',
        'min_num_of_enrollments',
        'modality',
        'parent_id',
        'schedules',
        'section_code',
        'slug',
        'start_date',
        'title',
        'partner.id',
        'partner.name',
        'partner.partner_id',
        'term.id',
        'term.name',
        'timezone',
        'lms_access_end_date',
        'lms_indefinite_access',
      ];
      break;

    case 'contentProvider':
      getMutation = rtkQApi.endpoints.getPaginatedContentProviders.useQuery;
      deleteMutation = rtkQApi.endpoints.deleteContentProvider.useMutation;
      break;

    case 'lmsInstance':
      getMutation = rtkQApi.endpoints.getPaginatedLMSInstances.useQuery;
      deleteMutation = rtkQApi.endpoints.deleteLMSInstance.useMutation;
      break;

    case 'location':
      getMutation = rtkQApi.endpoints.getPaginatedLocations.useQuery;
      deleteMutation = rtkQApi.endpoints.deleteLocation.useMutation;
      break;

    case 'enrollment':
      getMutation = rtkQApi.endpoints.getPaginatedEnrollments.useQuery;
      deleteMutation = () => [() => Promise.resolve({}), { data: null, isLoading: false }];
      filters = tableParams?.filters;
      break;

    case 'membershipSubscription':
      getMutation = rtkQApi.endpoints.getPaginatedMembershipSubscriptions.useQuery;
      deleteMutation = () => [() => Promise.resolve({}), { data: null, isLoading: false }];
      break;

    case 'waiver':
      getMutation = waiversApi.endpoints.getPaginatedWaivers.useQuery;
      deleteMutation = waiversApi.endpoints.deleteWaiver.useMutation;
      break;

    default:
      throw new Error(`Unsupported entity type: ${entityType}`);
  }

  const {
    isFetching,
    data: paginatedEntities,
    isLoading,
  } = getMutation(
    {
      partnerId,
      search: tableParams.search,
      page: tableParams.pagination.current,
      perPage: tableParams.pagination.pageSize,
      sort: tableParams.sort,
      filters,
      fields,
      ...queryArgs,
    },
    queryOptions,
  );
  const [deleteEntity] = deleteMutation();

  const searchInputRef = React.useRef(null);

  const handleSearchChange = (value: string): void => {
    setTableParams({
      ...tableParams,
      search: value,
      pagination: { ...tableParams.pagination, current: 1 },
    });
  };

  const handleDelete = (term: T): void => {
    setSelectedEntity(term);
    setIsDeleteEntityModalOpen(true);
  };

  const handleDropdownClick = (e: any, record: T): void => {
    if (e.key === 'edit') handleEdit(record);
    if (e.key === 'delete') handleDelete(record);
  };

  const handleModalClose = (): void => {
    setIsModalOpen(false);
    setSelectedEntity(undefined);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (selectedEntity) {
      await deleteEntity({ id: selectedEntity.id, partnerId });
      setIsDeleteEntityModalOpen(false);
      setSelectedEntity(undefined);
    }
  };

  const handleReset = (): void => {
    if (searchInputRef.current) {
      searchInputRef.current.clearInput();
    }
    handleSearchChange('');
  };

  return {
    searchInputRef,
    tableParams,
    setTableParams,
    selectedEntity,
    setSelectedEntity,
    isModalOpen,
    setIsModalOpen,
    isDeleteEntityModalOpen,
    setIsDeleteEntityModalOpen,
    isFetching,
    isLoading,
    paginatedEntities,
    handleSearchChange,
    handleEdit,
    handleDelete,
    handleDropdownClick,
    handleAdd,
    handleModalClose,
    handleDeleteConfirm,
    handleReset,
  };
}

// Generic interface for entity form props
interface EntityFormModalHookProps<T> {
  useFormPartnerId?: boolean;
  onClose?: () => void;
  entity?: T;
  resolver?: Resolver; // Custom resolver for validation
  dateFields?: string[]; // Fields that should be treated as dates
  entityType: EntityType;
  validationSchema?: yup.ObjectSchema<any>;
  defaultValues?: Partial<T>;
  formatDefaultValues?: (defaultValues: Record<string, any>) => Record<string, any>;
  formatPayload?: (data: T) => T;
  context?: any;
}

// Return type for the hook
interface EntityFormModalHook<T> {
  control: Control<FieldValues, any>;
  isLoading: boolean;
  handleSubmit: UseFormHandleSubmit<Record<string, any>>;
  onSubmit: (data: T) => void;
  isDirty: boolean;
  isSubmitted: boolean;
  isSubmitting: boolean;
  isSubmitSuccessful: boolean;
  setValue: UseFormSetValue<FieldValues>;
  clearErrors: any;
  mutationState: {
    isLoading: boolean;
    error: any | undefined;
    isSuccess: boolean;
    data: Record<string, any> | undefined;
  };
  watch: UseFormWatch<FieldValues>;
  errors: FieldErrors<FieldValues>;
  reset: (values?: Partial<T>) => void;
  resetField: (field?: string) => any;
  getImageFileList: (url: string) => UploadFile[];
  methods: any;
}

export function useEntityFormModal<T extends Record<string, any>>({
  onClose = () => {},
  entity,
  entityType,
  resolver,
  validationSchema,
  defaultValues,
  formatDefaultValues = null,
  formatPayload = null,
  useFormPartnerId = false,
  context = null,
}: EntityFormModalHookProps<T>): EntityFormModalHook<T> {
  const { partnerId } = useParams<{ partnerId: string }>();

  // Get the appropriate mutation hooks based on entity type
  let createMutation;
  let updateMutation;

  switch (entityType) {
    // TODO: We could use the entityType to dynamically import the correct mutation hooks?
    // e.g. rtkqApi.endpoints[`create${entityType}`].useMutation

    case 'term':
      createMutation = rtkQApi.endpoints.createTerm.useMutation;
      updateMutation = rtkQApi.endpoints.updateTerm.useMutation;
      break;

    case 'instructor':
      createMutation = rtkQApi.endpoints.createInstructor.useMutation;
      updateMutation = rtkQApi.endpoints.updateInstructor.useMutation;
      break;

    case 'invite':
      createMutation = rtkQApi.endpoints.createInvite.useMutation;
      // TODO: Duplicating but we should check if updateMutation exists before using the hook?
      updateMutation = rtkQApi.endpoints.createInvite.useMutation;
      break;

    case 'donationAsk':
      createMutation = rtkQApi.endpoints.createDonationAsk.useMutation;
      updateMutation = rtkQApi.endpoints.updateDonationAsk.useMutation;
      break;

    case 'contentProvider':
      createMutation = rtkQApi.endpoints.createContentProvider.useMutation;
      updateMutation = rtkQApi.endpoints.updateContentProvider.useMutation;
      break;

    case 'course':
      createMutation = rtkQApi.endpoints.createCourse.useMutation;
      updateMutation = rtkQApi.endpoints.updateCourse.useMutation;
      break;

    case 'lmsInstance':
      createMutation = rtkQApi.endpoints.createLMSInstance.useMutation;
      updateMutation = rtkQApi.endpoints.updateLMSInstance.useMutation;
      break;

    case 'location':
      createMutation = rtkQApi.endpoints.createLocation.useMutation;
      updateMutation = rtkQApi.endpoints.updateLocation.useMutation;
      break;

    case 'enrollment':
      // TODO: Update if create is implemented
      createMutation = () => [() => Promise.resolve({}), { data: null, isLoading: false }];
      updateMutation = rtkQApi.endpoints.updateEnrollment.useMutation;
      break;

    case 'membershipSubscription':
      createMutation = rtkQApi.endpoints.createMembershipSubscription.useMutation;
      updateMutation = () => [() => Promise.resolve({}), { data: null, isLoading: false }];
      break;

    case 'waiver':
      createMutation = waiversApi.endpoints.createWaiver.useMutation;
      updateMutation = waiversApi.endpoints.updateWaiver.useMutation;
      break;

    default:
      throw new Error(`Unsupported entity type: ${entityType}`);
  }

  // Set up mutations
  const [
    create,
    {
      data: createdEntity,
      isLoading: isCreatingLoading,
      error: createError,
      isSuccess: isCreateSuccess,
    },
  ] = createMutation();
  const [
    update,
    {
      data: updatedEntity,
      isLoading: isUpdatingLoading,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = updateMutation();

  const isLoading = isCreatingLoading || isUpdatingLoading;
  const mutationData = createdEntity || updatedEntity;
  const error = createError || updateError;
  const isSuccess = isCreateSuccess || isUpdateSuccess;

  // Process default values
  const getDefaultValues = (): Record<string, any> => {
    if (!entity) {
      return defaultValues || null;
    }

    const entityValues = {};
    Object.entries(entity).forEach(([key, value]) => {
      // Check if the value is a date string and not just a number (ex. 5745)
      // This is a workaround for the fact that moment.js does not parse date strings correctly
      const isDateField =
        moment(value, moment.ISO_8601, true).isValid() && Number.isNaN(Number(value));

      if (isDateField) {
        // Convert date strings to JS date objects
        entityValues[key] = moment(value);
      } else {
        entityValues[key] = value ?? '';
      }
    });

    if (formatDefaultValues) {
      return formatDefaultValues(entityValues);
    }
    return entityValues;
  };

  const getImageFileList = (url: string): UploadFile[] =>
    url
      ? [
          {
            uid: '-1',
            url,
            status: 'done',
            name: url.split('/').pop(),
          },
        ]
      : [];

  const formattedDefaultValues = getDefaultValues();

  // Set up form
  const methods = useForm({
    resolver: resolver || yupResolver(validationSchema),
    shouldFocusError: false,
    mode: 'onBlur',
    defaultValues: formattedDefaultValues,
    context,
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { isDirty, errors, isSubmitted, isSubmitting, isSubmitSuccessful },
    watch,
    resetField,
    reset,
    setError,
    clearErrors,
  } = methods;

  // Handle success effect
  React.useEffect(() => {
    if (mutationData && !isLoading) {
      onClose();
    }
  }, [mutationData, isLoading, onClose]);

  // Submit handler
  const onSubmit = (data: T): void => {
    const mutation = entity ? update : create;
    const formattedData = {
      ...data,
      ...(entity?.id ? { id: entity.id } : {}),
    };
    const payload = formatPayload ? formatPayload(formattedData) : formattedData;
    mutation({
      payload,
      partnerId: useFormPartnerId ? payload.partnerId : partnerId,
    })
      .unwrap()
      .catch((err) => {
        setFormFieldMutationError(err, setError);
      });
  };

  return {
    control,
    handleSubmit,
    onSubmit,
    isDirty,
    isLoading,
    isSubmitted,
    isSubmitting,
    isSubmitSuccessful,
    errors,
    setValue,
    clearErrors,
    watch,
    reset,
    resetField,
    getImageFileList,
    mutationState: {
      error,
      isSuccess,
      data: mutationData,
      isLoading,
    },
    methods,
  };
}

interface Filter {
  field: string;
  op: string;
  val: any;
}

interface Filters {
  [key: string]: any;
}

interface UseFiltersProps {
  tableParams: TableParamsInterface;
  setTableParams: React.Dispatch<React.SetStateAction<TableParamsInterface>>;
  filtersConfig: {
    [key: string]: string; // key: filter name, value: field path for filtering
  };
}

export function useFilters({ tableParams, setTableParams, filtersConfig }: UseFiltersProps) {
  const [filters, setFilters] = React.useState<Filters>(
    Object.keys(filtersConfig).reduce((acc, key) => ({ ...acc, [key]: null }), {}),
  );

  // Track if filters have changed to avoid unnecessary updates
  const [hasActiveFilters, setHasActiveFilters] = React.useState(false);

  React.useEffect(() => {
    const filtersList: Filter[] = Object.entries(filters)
      .filter(([key, filter]) => filter !== null && filter?.value !== null && key !== null)
      .map(([key, filter]) => ({
        field: filtersConfig[key],
        op: filter?.op || 'eq',
        val: filter?.value,
      }));

    const newHasActiveFilters = filtersList.length > 0;

    // Only update tableParams if the filter state has actually changed
    if (
      newHasActiveFilters !== hasActiveFilters ||
      (newHasActiveFilters &&
        JSON.stringify(tableParams.filters) !== JSON.stringify([{ and: filtersList }]))
    ) {
      setHasActiveFilters(newHasActiveFilters);

      if (newHasActiveFilters) {
        setTableParams((prev) => ({
          ...prev,
          filters: [{ and: filtersList }],
          pagination: { ...prev.pagination, current: 1 }, // Only reset to page 1 when filters change
        }));
      } else if (tableParams.filters?.length > 0) {
        setTableParams((prev) => ({ ...prev, filters: [] }));
      }
    }
  }, [filters, filtersConfig]);

  const handleFilterChange =
    (filterName: string, op = 'eq') =>
    (value: string) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [filterName]: {
          value: value || null, // Set to null if value is empty
          op, // Use the provided operator or default to 'eq'
        },
      }));
    };

  const resetFilters = () => {
    setFilters(Object.keys(filtersConfig).reduce((acc, key) => ({ ...acc, [key]: null }), {}));
  };

  return {
    filters,
    handleFilterChange,
    resetFilters,
  };
}
